import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import type { TAudioPlayerElement } from '@shared/dream-components';
import { AudioPlayerSerializer } from '@shared/dream-components';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import { LocalStorageKeys } from '~/interfaces/local_storage';
import type { PodcastShowEpisodePageLoaderData } from '~/routes/podcast/s/$showSlug/$episodeSlug';

import usePodcastMergeTagProcessor from './hooks/usePodcastMergeTagProcessor';

type Props = PropsWithChildren<{
  node: TAudioPlayerElement;
}>;

function getPlaybackStorageKey(episodeId: string | undefined): string | undefined {
  if (!episodeId) {
    return undefined;
  }

  return `${LocalStorageKeys.PODCAST_PLAYBACK}:${episodeId}`;
}

// TODO: Currently scoped to episode detail page only. To support the show listing
// page, make the loader data generic or detect the page context.
const AudioPlayer = ({ node, children }: Props) => {
  const { podcastEpisode } = useTypedLoaderData<PodcastShowEpisodePageLoaderData>();
  const { children: childrenToRender, node: nodeToRender } = usePodcastMergeTagProcessor({ node, children });
  const { t } = useTranslation();
  const isLoggedIn = useIsLoggedIn();
  const { handleOpenModal } = useGlobalModals();

  const playableUrl = podcastEpisode?.audio_url || podcastEpisode?.audio_preview_url || '';
  const hasPlayableAudio = playableUrl.length > 0;
  const isPreview = Boolean(
    podcastEpisode?.is_premium && !podcastEpisode.audio_url && podcastEpisode.audio_preview_url,
  );

  const handlePlayAttempt =
    !isLoggedIn && !hasPlayableAudio ? () => handleOpenModal('login', { preventScrollReset: true }) : undefined;

  const isPlaybackLocked = isLoggedIn && !hasPlayableAudio;
  const tooltipText = isPlaybackLocked ? t('podcasts.episode.audio_player.locked_tooltip') : undefined;
  const playbackStorageKey = getPlaybackStorageKey(podcastEpisode?.id);

  return (
    <AudioPlayerSerializer
      element={nodeToRender as TAudioPlayerElement}
      className={podcastEpisode?.is_premium ? 'podcast-episode-paywall-anchor' : undefined}
      onPlayAttempt={handlePlayAttempt}
      isPlaybackLocked={isPlaybackLocked}
      isPreview={isPreview}
      tooltipText={tooltipText}
      playbackStorageKey={playbackStorageKey}
    >
      {childrenToRender}
    </AudioPlayerSerializer>
  );
};

export default AudioPlayer;
